import { Box, Typography, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Badge, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CurrencyExchange, Payment, Search } from '@mui/icons-material';

import { ColumnTitle, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { useTransactions } from '../../../../services/transactions/useTransactions';
import { IUserDetail } from '../../../../types/User';
import { useState } from 'react';
import { formatPrice } from '../../../../utils/format';
import { capitalize } from 'lodash';
import { convertToDateTime } from '../../../../utils/datetime';
import RefundModal from '../../../../components/Modal/UserDetail/ModalRefund';
import { CardInfo, TTransaction } from '../../../../services/transactions/transaction.service';
import Copyable from '../../../../components/Copyable';
import TruncateMiddle from '../../../../components/TruncateMiddle';
import { useCurrentProfile } from '../../../../hooks/CommonContext';

const StatusColorMapping: Record<string, 'success' | 'warning' | 'error'> = {
    completed: 'success',
    pending: 'warning',
    failed: 'error',
    approved: 'success',
    rejected: 'error',
    captured: 'warning',
    released: 'success',
}

const TransactionActivity: Record<string, string> = {
    topup: 'Wallet top-up',
    fee_charging: 'Charging fee',
    fee_idle_penalty: 'Idle penalty',
    fee_no_show_penalty: 'No-show penalty',
    fee_charging_enters_next_reservation: 'Charging enters next reservation',
    deposit: 'Deposit',
    deposit_released: 'Deposit released',
    deposit_captured: 'Deposit captured',
    deposit_checked: 'Deposit checked',
    deposit_refunds: 'Deposit refunds',
    refund_by_admin: 'Refund by admin',
    subscription_payment: 'Subscription payment',
}

const PaymentMethodMapping: Record<string, string> = {
    card: 'Card',
    wallet: 'Wallet',
    Visa: 'Visa',
    MasterCard: 'MasterCard',
    'American Express': 'American Express',
}

const getLast4DigitCard = (paymentInfo?: string | CardInfo) => {
    if (!paymentInfo) return '-';
    if (typeof paymentInfo === 'string') {
        const parts = paymentInfo.split(' ');
        if (parts.length !== 3) return '-';
        return `**** **** ${parts[2]}`;
    }
    return `**** **** ${paymentInfo.last4}`;
}

export const Transactions = ({ userDetail }: { userDetail: IUserDetail }) => {
    const [searchParams, setSearchParams] = useState<{ [key: string]: any }>({});
    const adminProfile = useCurrentProfile();
    const [page, setPage] = useState(1);
    const { data: transactionData, isLoading, isFetching } = useTransactions(page, { user_id: userDetail._id!, per_page: 10, ...searchParams });
    const { data: tableData, total, per_page } = transactionData || { data: [], total: 0, per_page: 10 };
    const [openRefundModal, setOpenRefundModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TTransaction | null>(null);

    const checkRefundable = (row: TTransaction) => {
        if (adminProfile?.role !== 'Superadmin') return false;
        if (row.type === 'topup' && row.payment_type === 'wallet') return false;

        const excludedTypes = [
            // 'deposit',
            'deposit_refunds',
            'deposit_checked',
            'refund',
            'refund_by_admin'
        ];

        return row.status === 'Approved' &&
            row.transaction_id &&
            !excludedTypes.includes(row.type) &&
            row.amount > (row.refunded_amount || 0);
    }

    const onSearch = (params: { [key: string]: any }) => {
        setSearchParams(params);
        setPage(1);
    }
    return (
        <>
            <Box sx={{ color: 'white', p: 4 }}>
                <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                            <Payment style={{ fill: '#FFD600', width: 32, height: 32 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={600}>Transactions</Typography>
                            <Typography variant="body2" color="gray">Get a detailed breakdown of user transactions.</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            Search
                        </Button>
                        <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            Download CSV
                        </Button>
                        <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            <FilterListIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                    <Box mb={2}>
                        <WrapTable p={"5px 21px"} height="100%" marginBottom={3} isLoading={isLoading || isFetching}>
                            <TableContainer>
                                <ScrollbarCustom autoHide>
                                    <Table sx={{ minWidth: 800, marginBottom: "10px" }}>
                                        <TableHead>
                                            <TableRow>
                                                <ColumnTitle sx={{ pl: 0 }}>SN</ColumnTitle>
                                                <ColumnTitle
                                                    filter
                                                    onChangeText={(e) => onSearch({ ...searchParams, transaction_id: e })}
                                                >Transaction ID</ColumnTitle>
                                                <ColumnTitle
                                                    filter
                                                    onChangeText={(e) => onSearch({ ...searchParams, charge_session_id: e })}
                                                >Session ID</ColumnTitle>
                                                <ColumnTitle
                                                    filterOption={Object.entries(TransactionActivity).map(([key, value]) => ({ label: value, value: key }))}
                                                    onChangeText={(e) => onSearch({ ...searchParams, type: e })}
                                                >Activity</ColumnTitle>
                                                <ColumnTitle sx={{ minWidth: '200px' }}>Data & Time</ColumnTitle>
                                                <ColumnTitle
                                                    filterOption={Object.entries(PaymentMethodMapping).map(([key, value]) => ({ label: value, value: key }))}
                                                    onChangeText={(e) => onSearch({ ...searchParams, payment_type: e })}
                                                >Payment Method</ColumnTitle>
                                                <ColumnTitle
                                                    filter
                                                    onChangeText={(e) => onSearch({ ...searchParams, card_last4: e })}
                                                >Card Number</ColumnTitle>
                                                <ColumnTitle>Amount</ColumnTitle>
                                                <ColumnTitle >Refunded</ColumnTitle>
                                                <ColumnTitle
                                                    filter
                                                    onChangeText={(e) => onSearch({ ...searchParams, description: e })}
                                                >Description</ColumnTitle>
                                                <ColumnTitle align='center'
                                                    filterOption={Object.keys(StatusColorMapping).map((key) => ({ label: capitalize(key), value: capitalize(key) }))}
                                                    onChangeText={(e) => onSearch({ ...searchParams, status: e })}
                                                >Status</ColumnTitle>
                                                <ColumnTitle align='center'>Action</ColumnTitle>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tableData.map((row: TTransaction, index: number) => (
                                                <TableRow key={row._id}>
                                                    <TableCell sx={{ pl: 0 }}>{(page - 1) * per_page + index + 1}</TableCell>
                                                    <TableCell>{
                                                        <Copyable text={row.transaction_id} label={<TruncateMiddle text={row.transaction_id} />} />
                                                    }</TableCell>
                                                    <TableCell>{
                                                        row.charge_session_id ? (
                                                            <Copyable text={row.charge_session_id} label={row.charge_session_id} />
                                                        ) : ('-')
                                                    }</TableCell>
                                                    <TableCell>{TransactionActivity[row.type]}</TableCell>
                                                    <TableCell>
                                                        {
                                                            convertToDateTime(row.createdAt)
                                                        }
                                                    </TableCell>
                                                    <TableCell>{PaymentMethodMapping[row.payment_type] || row.payment_type}</TableCell>
                                                    <TableCell>{row.payment_type === PaymentMethodMapping.wallet ? '-' : getLast4DigitCard(row.payment_info)}</TableCell>
                                                    <TableCell>{row.amount && !['deposit_refunds', 'refund', 'refund_by_admin'].includes(row.type) ? formatPrice(row.amount) : '-'}
                                                        {row.refunded_amount ? (
                                                            <>
                                                                <br />
                                                                {row.refunded_amount ? `Refunded: ${formatPrice(row.refunded_amount)}` : null}
                                                            </>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>{row.amount && ['deposit_refunds', 'refund', 'refund_by_admin'].includes(row.type) ?
                                                        <>
                                                            {formatPrice(row.amount)}
                                                            <br />
                                                            {row.createdAt ? `${convertToDateTime(row.createdAt)}` : null}
                                                        </>
                                                        : '-'}</TableCell>
                                                    <TableCell>{row.description || '-'}</TableCell>
                                                    <TableCell align='center'>
                                                        <Badge badgeContent={capitalize(row.status)} color={StatusColorMapping[row.status?.toLowerCase()] || 'default'} sx={{ '.MuiBadge-badge': { width: '5rem' } }} />
                                                    </TableCell>
                                                    <TableCell align='center'>
                                                        {
                                                            checkRefundable(row) &&
                                                            (
                                                                <Tooltip title="Refund">
                                                                    <IconButton onClick={() => {
                                                                        setSelectedTransaction(row);
                                                                        setOpenRefundModal(true);
                                                                    }}
                                                                        sx={{ padding: '2px', color: '#FFD600' }}>
                                                                        <CurrencyExchange sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </ScrollbarCustom>
                            </TableContainer>
                        </WrapTable>
                        <Pagination
                            page={page}
                            setPage={setPage}
                            totalItems={total}
                            totalPage={Math.ceil(total / per_page)}
                            currentPage={page}
                        />
                    </Box>
                </Paper>
            </Box>
            <RefundModal open={openRefundModal} onClose={() => setOpenRefundModal(false)} userId={userDetail._id!} transaction={selectedTransaction} />
        </>
    );
}
